// src/pages/agent-api-new-chat.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Flex,
  Loader,
  SelectField,
  Text,
  View,
} from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import {
  useAgentApiAgentList,
  useAgentApiCreateConversation,
  ConversationPartialResults,
  Agents,
  Loadable,
  AgentMeta,
  ConversationMeta,
} from "@heartcheck/shared-react";
import { Container } from "../library/container";

const TOP_DISCLAIMER = (
  <>
    <strong>Educational only — not a medical device.</strong>{" "}
    This chat is for general wellness education and must not be used for
    diagnosis or treatment. If you have serious or rapidly worsening symptoms,
    call <strong>112</strong> (EU), <strong>911</strong> (USA) or seek urgent
    medical care.
  </>
);

/**
 * Normalize the hybrid return of useAgentApiAgentList()
 * into a Loadable-like object.
 */
function useNormalizedAgentList(): Loadable<any> {
  const raw: any = useAgentApiAgentList();

  const stateCandidate: any =
    raw && raw.state
      ? raw.state
      : Array.isArray(raw)
        ? raw[0]
        : raw;

  if (stateCandidate && typeof stateCandidate.isUnloaded === "function") {
    return stateCandidate as Loadable<any>;
  }

  return Loadable.unloaded<any>();
}

/**
 * Extract an array of agent objects from the Loadable value, regardless of
 * whether it’s an ObjRecord, array, or something with items().
 */
function extractAgentsArray(agentList: Loadable<any>): any[] {
  const value = agentList.value;
  if (!value) return [];

  if (typeof value.items === "function") {
    return value.items() ?? [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value.values === "function") {
    return Array.from(value.values());
  }

  return [];
}

/**
 * New Chat screen
 */
export function AIAgentNewChat() {
  const agentList = useNormalizedAgentList();
  const agents = useMemo(() => extractAgentsArray(agentList), [agentList]);

  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [sending, setSending] = useState(false);

  const agentIds = useMemo(
    () =>
      agents
        .filter((agent) => agent?.id && typeof agent.id === "string")
        .map((agent) => agent.id as string),
    [agents]
  );

  const createConversation = useAgentApiCreateConversation(agentIds);

  // Recoil setters
  const setPartialResults = useSetRecoilState(ConversationPartialResults);
  const setAgentState = useSetRecoilState(Agents);

  const navigate = useNavigate();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[NewChat] 🧠 Agent IDs loaded:", agentIds);
    // eslint-disable-next-line no-console
    console.log("[NewChat] 🎯 Selected agent:", selectedAgent);
  }, [agentIds, selectedAgent]);

  const isAgentValid = agentIds.includes(selectedAgent);

  const onCreate = async () => {
    const agentId = selectedAgent.trim();

    if (!agentId || !isAgentValid) {
      // eslint-disable-next-line no-console
      console.warn("[NewChat] 🚫 No valid agent selected");
      return;
    }

    try {
      setSending(true);
      // eslint-disable-next-line no-console
      console.log(`[NewChat] 🚀 Creating conversation with agent: ${agentId}`);

      const newChatId = await createConversation(agentId);
      if (!newChatId || typeof newChatId !== "string") {
        throw new Error("createConversation did not return a chatId string");
      }
      // eslint-disable-next-line no-console
      console.log(`[NewChat] ✅ Conversation created: ${newChatId}`);

      const initialAgentMeta: AgentMeta = {
        agentStartResponding: null,
        agentStopResponding: null,
        agentPartialMessage: "",
        updatedAt: new Date().toISOString(),
      };

      setPartialResults((prev: Record<string, ConversationMeta>) => {
        const nextConv: ConversationMeta = {
          activeAgentId: agentId,
          byAgent: {
            ...(prev[newChatId]?.byAgent ?? {}),
            [agentId]: {
              ...(prev[newChatId]?.byAgent?.[agentId] ?? {}),
              ...initialAgentMeta,
            },
          },
        };
        return { ...prev, [newChatId]: nextConv };
      });

      const selectedAgentObj = agents.find((a) => a.id === agentId);

      if (!selectedAgentObj || !Array.isArray(selectedAgentObj.actions)) {
        // eslint-disable-next-line no-console
        console.warn("[NewChat] ⚠️ Selected agent missing or has no actions:", {
          selectedAgentObj,
          allAgents: agents,
        });
      } else {
        const hasGreetingAction = selectedAgentObj.actions.some(
          (a: any) => a?.name === "agentStartResponding" || a?.name === "start"
        );

        if (hasGreetingAction) {
          const flagKey = `pending-greeting-${newChatId}`;
          try {
            localStorage.setItem(flagKey, "true");
            sessionStorage.setItem("debug-last-chat", newChatId);
          } catch {
            /* ignore */
          }
          // eslint-disable-next-line no-console
          console.log(`[NewChat] 📝 Set ${flagKey} = true`);
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            "[NewChat] ⚠️ No explicit greeting/start action found on selected agent:",
            {
              selectedAgentObj,
              actions: selectedAgentObj.actions,
            }
          );
        }
      }

      // Reset agent cache so View screen re-resolves cleanly (prevents stale names)
      setAgentState(Loadable.unloaded());

      const viewPath = `/chat/view/${newChatId}`;
      try {
        sessionStorage.setItem("debug-last-navigate", viewPath);
      } catch {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.log(`[NewChat] 🧭 Navigating to ${viewPath} after short delay...`);

      setTimeout(() => {
        navigate(viewPath);
        setTimeout(() => {
          try {
            // eslint-disable-next-line no-console
            console.log(
              "[NewChat] 📍 window.location.pathname:",
              window.location.pathname
            );
            if (window.location.pathname !== viewPath) {
              // eslint-disable-next-line no-console
              console.warn("[NewChat] ⚠️ Route mismatch detected!", {
                expected: viewPath,
                actual: window.location.pathname,
              });
            } else {
              // eslint-disable-next-line no-console
              console.log("[NewChat] ✅ Route OK");
            }
          } catch {
            /* noop */
          }
        }, 150);
      }, 100);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[NewChat] ❌ Failed to create conversation:", err);
      alert("Failed to start chat. See console for details.");
    } finally {
      setSending(false);
    }
  };

  if (agentList.isUnloaded() || agentList.loading === "loading") {
    return <Loader />;
  }

  if (agents.length === 0) {
    return <div>No agents are available right now.</div>;
  }

  return (
    <div style={{ height: "100%", padding: "1rem" }}>
      <Container heading="Select Start Chat To Begin">
        <View
          width="100%"
          maxWidth="600px"
          style={{ marginLeft: "0", marginRight: "auto" }}
        >
          <Alert
            variation="info"
            isDismissible={false}
            role="status"
            marginBottom="12px"
            borderRadius="8px"
          >
            {TOP_DISCLAIMER}{" "}
            <Badge size="small" variation="warning" marginLeft="6px">
              Wellness / Educational
            </Badge>
          </Alert>
        </View>

        <Flex
          direction="column"
          gap="1rem"
          style={{ width: "100%", maxWidth: "600px" }}
        >
          <SelectField
            label="Chat Session Option"
            placeholder="Choose A Chat"
            value={selectedAgent}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setSelectedAgent(e.target.value)
            }
            size="large"
            fontSize="large"
            style={{ width: "100%", fontSize: "1.125rem" }}
          >
            <option value="" disabled>
              -- Select an agent --
            </option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </SelectField>

          <Text as="small" color="neutral.70">
            By continuing you acknowledge this chat is{" "}
            <strong>wellness/educational only</strong> and not a substitute for
            care.
          </Text>

          <Flex justifyContent="flex-end">
            <Button
              disabled={!isAgentValid || !selectedAgent || sending}
              variation="primary"
              size="small"
              type="button"
              onClick={onCreate}
              style={{ minWidth: "160px" }}
              isLoading={sending}
            >
              Start Chat
            </Button>
          </Flex>
        </Flex>
      </Container>
    </div>
  );
}
